import { useState } from 'react';
import { useAuth } from '../lib/AuthContext';
import { cn } from '../lib/utils';
import { Mail, Lock, Loader2, AlertCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function Login() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);
    } catch (err) {
      // Handle common Supabase auth errors with user-friendly messages
      const errorMessage = err?.message?.toLowerCase() || '';
      if (errorMessage.includes('invalid') || errorMessage.includes('credentials')) {
        setError('Invalid email or password');
      } else if (errorMessage.includes('email not confirmed')) {
        setError('Please verify your email address');
      } else if (errorMessage.includes('too many requests')) {
        setError('Too many attempts. Please try again later');
      } else {
        setError('Invalid email or password');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <img 
            src="https://res.cloudinary.com/dq4ihdqwt/image/upload/v1766574405/Untitled_design_2_jbpydg.png" 
            alt="Ecom Factory" 
            className="w-12 h-12"
          />
          <span className="text-2xl font-bold text-[#1E1B4B] font-['Manrope']">
            Ecom Factory
          </span>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-[#1E1B4B] font-['Manrope'] mb-2">
              Welcome Back
            </h1>
            <p className="text-slate-500 text-sm">
              Sign in to access your dashboard
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-11 py-3 border-2 border-slate-200 focus:border-cyan-500 focus:ring-cyan-500"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-11 py-3 border-2 border-slate-200 focus:border-cyan-500 focus:ring-cyan-500"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={cn(
                'w-full py-3 bg-cyan-500 hover:bg-cyan-600 text-white font-semibold rounded-lg',
                'transition-all shadow-lg shadow-cyan-500/30',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'flex items-center justify-center gap-2'
              )}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-slate-500">
            Internal tool - Contact admin for access
          </p>
        </div>
      </div>
    </div>
  );
}
